import {ApplicationConfig, ErrorHandler, inject, isDevMode, LOCALE_ID, provideAppInitializer, provideZoneChangeDetection} from '@angular/core';
import {provideRouter, Router, TitleStrategy, withPreloading} from '@angular/router';
import {routes} from './app.routes';
import {SelectivePreloadingStrategy} from './shared/services/selective-preloading.strategy';
import * as Sentry from "@sentry/angular";
import {provideHttpClient, withInterceptors} from "@angular/common/http";
import {DatePipe, IMAGE_CONFIG, IMAGE_LOADER, ImageLoaderConfig} from "@angular/common";
import {TitleService} from "./shared/services/title.service";
import {loadingIndicatorInterceptor} from "./shared/interceptors/loading-indicator.interceptor";
import {authInterceptor} from "./shared/interceptors/auth.interceptor";
import {LocaleService} from "./shared/services/locale.service";
import {statusInterceptor} from "./shared/interceptors/status.interceptor";
import {provideAnimations} from '@angular/platform-browser/animations';
import {provideStoreDevtools} from '@ngrx/store-devtools';
import {loadMediaUrl} from "./shared/utils/media-url-loader";
import {ApiProbeService} from './shared/services/api-probe.service';
import {SocketService} from './shared/services/socket.service';
import {LanguageService} from './shared/services/language.service';
import {firstValueFrom} from 'rxjs';
import {validationAwareErrorHandler} from './shared/services/validation-aware-error-handler';

export const appConfig: ApplicationConfig = {
  providers: [
    provideHttpClient(
      withInterceptors([authInterceptor, loadingIndicatorInterceptor, statusInterceptor])
    ),
    provideAppInitializer(() => {
      const apiProbeService = inject(ApiProbeService);
      const languageService = inject(LanguageService);
      return firstValueFrom(apiProbeService.probeCurrentApiHealth()).then(() => languageService.init());
    }),
    provideAppInitializer(() => {
      inject(SocketService);
    }),
    provideAnimations(),
    provideZoneChangeDetection({eventCoalescing: true}),
    provideRouter(routes, withPreloading(SelectivePreloadingStrategy)),
    // Sentry error handling
    {
      provide: ErrorHandler,
      useFactory: () => validationAwareErrorHandler(Sentry.createErrorHandler({
        showDialog: false,
      })),
    }, {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: IMAGE_CONFIG,
      useValue: {
        breakpoints: [800, 1440],
      }
    },
    {
      provide: IMAGE_LOADER,
      useValue: (config: ImageLoaderConfig): string => loadMediaUrl(config)
    },
    {
      provide: LOCALE_ID,
      deps: [LocaleService],
      useFactory: (localeService: LocaleService) => localeService.factoryProvider(),
    },
    // Page title
    {provide: TitleStrategy, useClass: TitleService},
    DatePipe,
    ...(isDevMode() ? [provideStoreDevtools({
      name: 'HumanstarsApp DevTools',
      logOnly: true,
      maxAge: 25,
      connectInZone: true,
    })] : []),
  ]
};
